import { t } from '../lib/i18n';
import {
  CONDITION_OPERATORS,
  VALUELESS_OPERATORS,
  type Condition,
  type ConditionGroup,
  type ConditionOperator,
} from './api';

/**
 * A group of tests, as rows.
 *
 * The field is a list rather than a text box: the paths a trigger offers come from the server's
 * own catalogue, and somebody typing `event.payload.leadid` builds an automation that never fires
 * and never says why. A path not on the list stays editable — an automation built before a field
 * was catalogued must still open — but nobody has to invent one.
 */
export function ConditionEditor({
  value,
  fields,
  onChange,
  emptyHint,
}: {
  value: ConditionGroup;
  /** The paths this trigger offers, from `/v1/automations/triggers`. */
  fields: readonly string[];
  onChange: (next: ConditionGroup) => void;
  emptyHint: string;
}): React.JSX.Element {
  const set = (index: number, change: Partial<Condition>): void => {
    onChange({
      ...value,
      conditions: value.conditions.map((condition, at) =>
        at === index ? { ...condition, ...change } : condition,
      ),
    });
  };

  return (
    <div className="conditions">
      <label className="cell-inline small">
        <span className="muted">{t('automations.match')}</span>
        <select
          className="select select--compact"
          value={value.match}
          onChange={(event) => onChange({ ...value, match: event.target.value as 'ALL' | 'ANY' })}
        >
          <option value="ALL">{t('automations.match.ALL')}</option>
          <option value="ANY">{t('automations.match.ANY')}</option>
        </select>
      </label>

      {value.conditions.length === 0 ? (
        <p className="muted small">{emptyHint}</p>
      ) : (
        <ul className="conditions__list">
          {value.conditions.map((condition, index) => {
            const known = fields.includes(condition.field);

            return (
              <li key={index} className="conditions__row">
                <select
                  className="select select--compact"
                  value={known ? condition.field : '__other'}
                  aria-label={t('automations.field')}
                  onChange={(event) =>
                    set(index, {
                      field: event.target.value === '__other' ? '' : event.target.value,
                    })
                  }
                >
                  {fields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                  <option value="__other">{t('automations.otherField')}</option>
                </select>

                {!known && (
                  <input
                    value={condition.field}
                    placeholder="event.payload.algumaCoisa"
                    aria-label={t('automations.field')}
                    maxLength={200}
                    onChange={(event) => set(index, { field: event.target.value })}
                  />
                )}

                <select
                  className="select select--compact"
                  value={condition.operator}
                  aria-label={t('automations.operator')}
                  onChange={(event) =>
                    set(index, { operator: event.target.value as ConditionOperator })
                  }
                >
                  {CONDITION_OPERATORS.map((operator) => (
                    <option key={operator} value={operator}>
                      {t(`automations.op.${operator}`)}
                    </option>
                  ))}
                </select>

                {/* "is empty" takes no value; a box there would invite somebody to fill it in. */}
                {!VALUELESS_OPERATORS.has(condition.operator) && (
                  <input
                    value={condition.value === undefined ? '' : String(condition.value)}
                    placeholder={t('automations.value')}
                    aria-label={t('automations.value')}
                    maxLength={500}
                    onChange={(event) => set(index, { value: event.target.value })}
                  />
                )}

                <button
                  type="button"
                  className="chip__remove"
                  aria-label={t('automations.removeCondition')}
                  onClick={() =>
                    onChange({
                      ...value,
                      conditions: value.conditions.filter((_, at) => at !== index),
                    })
                  }
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        className="button button--quiet button--compact"
        onClick={() =>
          onChange({
            ...value,
            conditions: [
              ...value.conditions,
              { field: fields[0] ?? 'event.type', operator: 'equals', value: '' },
            ],
          })
        }
      >
        {t('automations.addCondition')}
      </button>
    </div>
  );
}
